/**
 * WS-K soft-launch gate — UNIVERSAL (server + client safe) visibility primitives.
 *
 * The whole WS-K cluster (Open Questions board · Contribute · Member console · Contributors
 * leaderboard/profiles) ships INSIDE one merge but stays hidden from the public until Lam flips
 * the launch flag. Two layers enforce that:
 *
 *   1. SERVER ENFORCEMENT (the real gate) — `getWskAccess()` in `wsk-gate.ts`. A non-preview
 *      public request gets the ComingSoonGate placeholder BEFORE any data is fetched, so no
 *      board/leaderboard content ever reaches the HTML/RSC payload.
 *   2. NAV/FOOTER COSMETICS — `useWskNavVisible()` in `wsk-nav-visible.ts` (a client hook) hides
 *      the WS-K links in the shell nav rail + footer so the surfaces aren't *discoverable*. UX only.
 *
 * This module holds ONLY the shared, dependency-free pieces (cookie names + the flag reader) so it
 * is safe to import from BOTH the server gate / preview route AND the client hook — the React hook
 * lives in `wsk-nav-visible.ts` so importing a hook into a Server Component never happens here.
 */

/**
 * The httpOnly server-readable preview cookie — the REAL preview signal the server gate trusts
 * (set by `/preview?key=…`; not readable by client JS, so it can't be forged by XSS).
 */
export const WSK_PREVIEW_COOKIE = 'wsk_preview';

/**
 * A non-httpOnly COMPANION cookie set alongside the real one, purely so the client shell can
 * REVEAL the nav/footer links during an operator preview. It grants no access on its own — the
 * server gate never reads it; it only flips cosmetic link visibility. Set/cleared with the real
 * cookie by `/preview`.
 */
export const WSK_PREVIEW_UI_COOKIE = 'wsk_preview_ui';

/**
 * The one launch flag. `NEXT_PUBLIC_` so it's inlined at build for BOTH the server gate and the
 * client nav — when it's `'true'`, server + client agree immediately (no hydration mismatch) and
 * the surfaces are fully public. Default OFF.
 */
export function isWskEnabledFlag(): boolean {
  return process.env.NEXT_PUBLIC_WSK_ENABLED === 'true';
}

/**
 * Read the non-httpOnly `wsk_preview_ui` mirror cookie (set alongside the real httpOnly
 * `wsk_preview` at /preview). CLIENT-ONLY signal for revealing preview-gated UI; returns
 * false during SSR. Grants no server access on its own — the server gate never trusts it.
 * MUST be called from an effect (never during render) so SSR + first client render agree.
 */
export function hasPreviewAccessClient(): boolean {
  if (typeof document === 'undefined') return false;
  const name = WSK_PREVIEW_UI_COOKIE;
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) === '1' : false;
}

/**
 * PURE decision for whether the dossier owner card renders (client-side): the launch flag,
 * OR an operator preview, OR verified staff. Deny by default → flag-off public HTML stays
 * byte-neutral (the guard returns null on SSR + first client render). Mirrors decideWskAccess.
 */
export function decideOwnerCardVisible(input: {
  enabled: boolean;
  previewAccess: boolean;
  isStaff: boolean;
}): boolean {
  return input.enabled || input.previewAccess || input.isStaff;
}
